import { Link } from 'react-router';
import { ArrowIcon } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Feedback';
import type { ProductCard as Product } from '@/types/api';
import { ProductVisual } from './ProductVisual';

export function ProductCard({ product }: { product: Product }) {
  const primarySpec = product.specifications[0];
  return (
    <article className="group relative flex flex-col">
      <div className="relative overflow-hidden rounded-[8px]">
        <ProductVisual product={product} className="aspect-[4/3.2]" />
        {product.moq && (
          <span className="absolute top-3 left-3 rounded-[4px] bg-white/95 px-2.5 py-1 text-[11.5px] font-semibold text-primary shadow-[0_1px_2px_rgba(11,23,38,0.08)]">
            MOQ {product.moq}
          </span>
        )}
      </div>
      <div className="mt-5 flex flex-1 flex-col">
        <p className="text-[12px] font-semibold tracking-[0.14em] text-accent-dark uppercase">{product.category.name}</p>
        <h3 className="mt-1.5 font-display text-[19px] leading-snug font-bold tracking-tight text-primary">
          <Link to={`/products/${product.slug}`} className="after:absolute after:inset-0 focus-visible:outline-none">
            {product.name}
          </Link>
        </h3>
        {primarySpec && (
          <p className="mt-1.5 text-[14px] text-muted">
            {primarySpec.label}: <span className="text-ink-soft">{primarySpec.value}</span>
          </p>
        )}
        {product.customizationOptions.length > 0 && (
          <p className="mt-1 line-clamp-1 text-[13.5px] text-muted">{product.customizationOptions.slice(0, 4).join(' · ')}</p>
        )}
        <span className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-primary underline-offset-4 group-hover:underline">
          View details <ArrowIcon />
        </span>
      </div>
    </article>
  );
}

export function ProductGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Loading products">
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <Skeleton className="aspect-[4/3.2] rounded-[8px]" />
          <div className="mt-5 space-y-2">
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
